import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getLeads,
  getLeadsCount,
  getStatuses,
  getSellers,
  getCampaigns,
  getSources,
  deleteLead,
  bulkAssignLeads,
  bulkAutoAssignLeads,
  bulkDeleteLeads,
  exportLeadsCsv,
  updateLead,
  toggleLeadMarker,
  clearAllMarkers,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { formatRelativeTime, getInitials } from "../lib/utils";
import {
  Search,
  Plus,
  MoreHorizontal,
  Eye,
  Trash2,
  Download,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  MessageSquare,
  Phone,
  Mail,
  Megaphone,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  Clock,
  RefreshCw,
  Cake,
  Shuffle,
  Package,
  Bookmark,
  BookmarkCheck,
  BookmarkX,
} from "lucide-react";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { LeadFormDialog } from "../components/leads/LeadFormDialog";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Format birth date - preserve partial dates (year only, etc.)
const formatBirthDate = (dateStr) => {
  if (!dateStr) return "-";
  // If it's just a year (4 digits)
  if (/^\d{4}$/.test(dateStr.trim())) {
    return dateStr.trim();
  }
  // If it's a partial date like "1946" stored as ISO, extract just the year
  if (dateStr.includes("T") || dateStr.includes("-")) {
    const date = new Date(dateStr);
    // Check if it's a "fake" full date (Jan 1st)
    if (date.getMonth() === 0 && date.getDate() === 1) {
      return date.getFullYear().toString();
    }
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }
  return dateStr;
};

export const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Assignment dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [resetStatusOnAssign, setResetStatusOnAssign] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // Auto-assign dialog state
  const [autoAssignDialogOpen, setAutoAssignDialogOpen] = useState(false);
  const [selectedSellerIds, setSelectedSellerIds] = useState([]);
  const [resetStatusOnAutoAssign, setResetStatusOnAutoAssign] = useState(false);
  const [shuffleBeforeAssign, setShuffleBeforeAssign] = useState(false);

  // Filters - Multi-select arrays (initialized from URL params for persistence)
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedStatusIds, setSelectedStatusIds] = useState(() => {
    const param = searchParams.get("statusIds");
    return param ? param.split(",").filter(Boolean) : [];
  });
  const [selectedSellerFilterIds, setSelectedSellerFilterIds] = useState(() => {
    const param = searchParams.get("sellerIds");
    return param ? param.split(",").filter(Boolean) : [];
  });
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(() => {
    const param = searchParams.get("campaignIds");
    return param ? param.split(",").filter(Boolean) : [];
  });
  const [selectedSourceIds, setSelectedSourceIds] = useState(() => {
    const param = searchParams.get("sourceIds");
    return param ? param.split(",").filter(Boolean) : [];
  });
  const [cityFilter, setCityFilter] = useState(searchParams.get("city") || "");
  const [hasNotesFilter, setHasNotesFilter] = useState(searchParams.get("hasNotes") || "all");
  const [markedFilter, setMarkedFilter] = useState(searchParams.get("marked") || "all"); // "all", "marked", "unmarked"
  const [dateFrom, setDateFrom] = useState(searchParams.get("dateFrom") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("dateTo") || "");
  const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [showFilters, setShowFilters] = useState(() => {
    // Auto-show filters panel if any filter is active
    return searchParams.get("statusIds") || searchParams.get("sellerIds") || 
           searchParams.get("campaignIds") || searchParams.get("sourceIds") ||
           searchParams.get("city") || searchParams.get("hasNotes") || 
           searchParams.get("marked") || searchParams.get("dateFrom") || searchParams.get("dateTo");
  });
  
  // Sorting
  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "createdAt");
  const [sortOrder, setSortOrder] = useState(searchParams.get("sortOrder") || "desc");
  
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit")) || 20);
  const pageSizeOptions = [20, 30, 40, 50, 100, 200, 500];

  // Sync filters to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (search) params.set("search", search);
    if (selectedStatusIds.length > 0) params.set("statusIds", selectedStatusIds.join(","));
    if (selectedSellerFilterIds.length > 0) params.set("sellerIds", selectedSellerFilterIds.join(","));
    if (selectedCampaignIds.length > 0) params.set("campaignIds", selectedCampaignIds.join(","));
    if (selectedSourceIds.length > 0) params.set("sourceIds", selectedSourceIds.join(","));
    if (cityFilter) params.set("city", cityFilter);
    if (hasNotesFilter !== "all") params.set("hasNotes", hasNotesFilter);
    if (markedFilter !== "all") params.set("marked", markedFilter);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (page > 1) params.set("page", page.toString());
    if (limit !== 20) params.set("limit", limit.toString());
    if (sortBy !== "createdAt") params.set("sortBy", sortBy);
    if (sortOrder !== "desc") params.set("sortOrder", sortOrder);
    
    setSearchParams(params, { replace: true });
  }, [search, selectedStatusIds, selectedSellerFilterIds, selectedCampaignIds, selectedSourceIds, cityFilter, hasNotesFilter, markedFilter, dateFrom, dateTo, page, limit, sortBy, sortOrder, setSearchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sortBy, sortOrder };
      if (search) params.search = search;
      // Multi-select filters
      if (selectedStatusIds.length > 0) params.statusIds = selectedStatusIds.join(",");
      if (selectedCampaignIds.length > 0) params.campaignIds = selectedCampaignIds.join(",");
      if (selectedSellerFilterIds.length > 0) params.sellerIds = selectedSellerFilterIds.join(",");
      if (selectedSourceIds.length > 0) params.sourceIds = selectedSourceIds.join(",");
      if (cityFilter) params.city = cityFilter;
      if (hasNotesFilter !== "all") params.hasNotes = hasNotesFilter === "yes";
      if (markedFilter !== "all") params.isMarked = markedFilter === "marked";
      if (dateFrom) params.createdFrom = new Date(dateFrom).toISOString();
      if (dateTo) {
        // Set to end of day
        const endDate = new Date(dateTo);
        endDate.setHours(23, 59, 59, 999);
        params.createdTo = endDate.toISOString();
      }

      const [leadsRes, countRes, statusesRes, campaignsRes] = await Promise.all([
        getLeads(params),
        getLeadsCount(params),
        getStatuses(),
        getCampaigns(),
      ]);

      setLeads(leadsRes.data);
      setTotalCount(countRes.data.count);
      setStatuses(statusesRes.data);
      setCampaigns(campaignsRes.data);

      if (isAdmin) {
        const [sellersRes, sourcesRes] = await Promise.all([
          getSellers(),
          getSources(),
        ]);
        setSellers(sellersRes.data);
        setSources(sourcesRes.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des leads");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, selectedStatusIds, selectedSellerFilterIds, selectedCampaignIds, selectedSourceIds, cityFilter, hasNotesFilter, markedFilter, dateFrom, dateTo, sortBy, sortOrder, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchParams.get("new") === "true") {
      setFormDialogOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  const handleDelete = async () => {
    if (!leadToDelete) return;
    try {
      await deleteLead(leadToDelete);
      toast.success("Lead supprimé");
      setDeleteDialogOpen(false);
      setLeadToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    try {
      await bulkDeleteLeads(selectedLeads);
      toast.success(`${selectedLeads.length} lead(s) supprimé(s)`);
      setBulkDeleteDialogOpen(false);
      setSelectedLeads([]);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openAssignDialog = (sellerId, unassign = false) => {
    setSelectedSellerId(sellerId);
    setIsUnassigning(unassign);
    setResetStatusOnAssign(false);
    setAssignDialogOpen(true);
  };

  const handleBulkAssign = async () => {
    if (selectedLeads.length === 0) return;
    try {
      const result = await bulkAssignLeads(
        selectedLeads, 
        isUnassigning ? null : selectedSellerId, 
        resetStatusOnAssign
      );
      
      let message = `${selectedLeads.length} leads ${isUnassigning ? "désattribués" : "attribués"}`;
      if (resetStatusOnAssign && result.data.newStatusName) {
        message += ` et statut remis à "${result.data.newStatusName}"`;
      }
      toast.success(message);
      
      setAssignDialogOpen(false);
      setSelectedLeads([]);
      setResetStatusOnAssign(false);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'attribution");
    }
  };

  const openAutoAssignDialog = () => {
    setSelectedSellerIds([]);
    setResetStatusOnAutoAssign(false);
    setShuffleBeforeAssign(false);
    setAutoAssignDialogOpen(true);
  };

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleAutoAssign = async () => {
    if (selectedLeads.length === 0 || selectedSellerIds.length === 0) return;
    try {
      // Send shuffle option to backend - it will ensure leads go to DIFFERENT sellers
      const result = await bulkAutoAssignLeads(
        selectedLeads,
        selectedSellerIds,
        resetStatusOnAutoAssign,
        shuffleBeforeAssign  // This tells backend to assign to different sellers
      );
      
      let message = `${selectedLeads.length} leads distribués entre ${selectedSellerIds.length} vendeurs`;
      if (shuffleBeforeAssign) {
        message += " (mixés vers nouveaux vendeurs)";
      }
      if (resetStatusOnAutoAssign && result.data.newStatusName) {
        message += ` - statut: "${result.data.newStatusName}"`;
      }
      toast.success(message);
      
      setAutoAssignDialogOpen(false);
      setSelectedLeads([]);
      setSelectedSellerIds([]);
      setResetStatusOnAutoAssign(false);
      setShuffleBeforeAssign(false);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'attribution automatique");
    }
  };

  const toggleSellerForAutoAssign = (sellerId) => {
    setSelectedSellerIds(prev => 
      prev.includes(sellerId) 
        ? prev.filter(id => id !== sellerId)
        : [...prev, sellerId]
    );
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (selectedStatusIds.length > 0) params.statusIds = selectedStatusIds.join(",");
      if (selectedSellerFilterIds.length > 0) params.sellerIds = selectedSellerFilterIds.join(",");
      if (selectedCampaignIds.length > 0) params.campaignIds = selectedCampaignIds.join(",");
      if (selectedSourceIds.length > 0) params.sourceIds = selectedSourceIds.join(",");

      const response = await exportLeadsCsv(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leads_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleQuickStatusChange = async (leadId, newStatusId) => {
    setUpdatingStatus(leadId);
    try {
      await updateLead(leadId, { statusId: newStatusId });
      toast.success("Statut mis à jour");
      setLeads((prev) =>
        prev.map((lead) => {
          if (lead.id === leadId) {
            const newStatus = statuses.find((s) => s.id === newStatusId);
            return {
              ...lead,
              statusId: newStatusId,
              statusName: newStatus?.name,
              statusColor: newStatus?.color,
              updatedAt: new Date().toISOString(),
            };
          }
          return lead;
        })
      );
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleToggleMarker = async (leadId) => {
    try {
      const response = await toggleLeadMarker(leadId);
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId ? { ...lead, isMarked: response.data.isMarked } : lead
        )
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Erreur lors du marquage");
    }
  };

  const handleClearAllMarkers = async () => {
    try {
      const response = await clearAllMarkers();
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors du retrait des marqueurs");
    }
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((l) => l.id));
    }
  };

  const toggleSelectLead = (leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return sortOrder === "desc" 
      ? <ArrowDown className="h-4 w-4 ml-1" />
      : <ArrowUp className="h-4 w-4 ml-1" />;
  };

  const totalPages = Math.ceil(totalCount / limit);

  const clearFilters = () => {
    setSearch("");
    setSelectedStatusIds([]);
    setSelectedSellerFilterIds([]);
    setSelectedCampaignIds([]);
    setSelectedSourceIds([]);
    setCityFilter("");
    setHasNotesFilter("all");
    setMarkedFilter("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasFilters = search || selectedStatusIds.length > 0 || selectedSellerFilterIds.length > 0 || selectedCampaignIds.length > 0 || selectedSourceIds.length > 0 || cityFilter || hasNotesFilter !== "all" || markedFilter !== "all" || dateFrom || dateTo;
  const activeFiltersCount = [
    search,
    selectedStatusIds.length > 0,
    selectedSellerFilterIds.length > 0,
    selectedCampaignIds.length > 0,
    selectedSourceIds.length > 0,
    cityFilter,
    hasNotesFilter !== "all",
    markedFilter !== "all",
    dateFrom,
    dateTo
  ].filter(Boolean).length;

  // Get active sellers only for auto-assign
  const activeSellers = sellers.filter(s => s.isActive !== false);

  // Count marked leads in current view
  const markedLeadsCount = leads.filter(l => l.isMarked).length;

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setLimit(parseInt(newSize));
    setPage(1); // Reset to first page when changing page size
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Leads</h1>
          <p className="text-muted-foreground mt-1">
            {hasFilters ? (
              <>{totalCount} lead{totalCount !== 1 ? "s" : ""} trouvé{totalCount !== 1 ? "s" : ""}</>
            ) : (
              <>{totalCount} lead{totalCount !== 1 ? "s" : ""} au total</>
            )}
            {markedLeadsCount > 0 && (
              <span className="ml-2 text-orange-500">
                • {markedLeadsCount} marqué{markedLeadsCount !== 1 ? "s" : ""} sur cette page
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {markedFilter === "marked" && (
            <Button 
              variant="outline" 
              onClick={handleClearAllMarkers}
              className="text-orange-500 border-orange-500 hover:bg-orange-50"
            >
              <BookmarkX className="h-4 w-4 mr-2" />
              Retirer tous les marqueurs
            </Button>
          )}
          <Button variant="outline" onClick={handleExport} data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </Button>
          <Button onClick={() => setFormDialogOpen(true)} data-testid="add-lead-button">
            <Plus className="h-4 w-4 mr-2" />
            Nouveau lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, email, téléphone, source..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtres
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
              <Button type="submit" data-testid="search-button">
                Rechercher
              </Button>
            </div>

            {showFilters && (
              <div className="space-y-4 pt-3 border-t">
                {/* Row 1: Main filters */}
                <div className="flex flex-wrap gap-3">
                  {/* Multi-select Status Filter */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[180px] justify-between" data-testid="status-filter">
                        {selectedStatusIds.length === 0 ? (
                          "Tous les statuts"
                        ) : selectedStatusIds.length === 1 ? (
                          statuses.find(s => s.id === selectedStatusIds[0])?.name
                        ) : (
                          `${selectedStatusIds.length} statuts`
                        )}
                        <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[220px] p-2" align="start">
                      <div className="space-y-1">
                        <button
                          onClick={() => { setSelectedStatusIds([]); setPage(1); }}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors ${selectedStatusIds.length === 0 ? "bg-muted" : ""}`}
                        >
                          Tous les statuts
                        </button>
                        {statuses.map((status) => (
                          <button
                            key={status.id}
                            onClick={() => {
                              setSelectedStatusIds(prev => 
                                prev.includes(status.id) 
                                  ? prev.filter(id => id !== status.id)
                                  : [...prev, status.id]
                              );
                              setPage(1);
                            }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors"
                          >
                            <Checkbox checked={selectedStatusIds.includes(status.id)} />
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
                            {status.name}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Multi-select Campaign Filter */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[180px] justify-between" data-testid="campaign-filter">
                        {selectedCampaignIds.length === 0 ? (
                          "Toutes les campagnes"
                        ) : selectedCampaignIds.length === 1 ? (
                          campaigns.find(c => c.id === selectedCampaignIds[0])?.name
                        ) : (
                          `${selectedCampaignIds.length} campagnes`
                        )}
                        <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[220px] p-2" align="start">
                      <div className="space-y-1">
                        <button
                          onClick={() => { setSelectedCampaignIds([]); setPage(1); }}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors ${selectedCampaignIds.length === 0 ? "bg-muted" : ""}`}
                        >
                          Toutes les campagnes
                        </button>
                        {campaigns.map((campaign) => (
                          <button
                            key={campaign.id}
                            onClick={() => {
                              setSelectedCampaignIds(prev => 
                                prev.includes(campaign.id) 
                                  ? prev.filter(id => id !== campaign.id)
                                  : [...prev, campaign.id]
                              );
                              setPage(1);
                            }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors"
                          >
                            <Checkbox checked={selectedCampaignIds.includes(campaign.id)} />
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: campaign.color }} />
                            {campaign.name}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Multi-select Seller Filter - Admin only */}
                  {isAdmin && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-[180px] justify-between" data-testid="seller-filter">
                          {selectedSellerFilterIds.length === 0 ? (
                            "Tous les vendeurs"
                          ) : selectedSellerFilterIds.length === 1 ? (
                            selectedSellerFilterIds[0] === "unassigned" 
                              ? "Non attribués" 
                              : sellers.find(s => s.id === selectedSellerFilterIds[0])?.name
                          ) : (
                            `${selectedSellerFilterIds.length} vendeurs`
                          )}
                          <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[220px] p-2" align="start">
                        <div className="space-y-1 max-h-[250px] overflow-y-auto">
                          <button
                            onClick={() => { setSelectedSellerFilterIds([]); setPage(1); }}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors ${selectedSellerFilterIds.length === 0 ? "bg-muted" : ""}`}
                          >
                            Tous les vendeurs
                          </button>
                          <button
                            onClick={() => {
                              setSelectedSellerFilterIds(prev => 
                                prev.includes("unassigned") 
                                  ? prev.filter(id => id !== "unassigned")
                                  : [...prev, "unassigned"]
                              );
                              setPage(1);
                            }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors"
                          >
                            <Checkbox checked={selectedSellerFilterIds.includes("unassigned")} />
                            <span className="italic text-muted-foreground">Non attribués</span>
                          </button>
                          {sellers.map((seller) => (
                            <button
                              key={seller.id}
                              onClick={() => {
                                setSelectedSellerFilterIds(prev => 
                                  prev.includes(seller.id) 
                                    ? prev.filter(id => id !== seller.id)
                                    : [...prev, seller.id]
                                );
                                setPage(1);
                              }}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors"
                            >
                              <Checkbox checked={selectedSellerFilterIds.includes(seller.id)} />
                              {seller.name}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}

                  {/* Multi-select Source Filter - Admin only */}
                  {isAdmin && sources.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-[180px] justify-between" data-testid="source-filter">
                          {selectedSourceIds.length === 0 ? (
                            "Toutes les sources"
                          ) : selectedSourceIds.length === 1 ? (
                            sources.find(s => s.id === selectedSourceIds[0])?.name
                          ) : (
                            `${selectedSourceIds.length} sources`
                          )}
                          <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[220px] p-2" align="start">
                        <div className="space-y-1 max-h-[250px] overflow-y-auto">
                          <button
                            onClick={() => { setSelectedSourceIds([]); setPage(1); }}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors ${selectedSourceIds.length === 0 ? "bg-muted" : ""}`}
                          >
                            Toutes les sources
                          </button>
                          {sources.map((source) => (
                            <button
                              key={source.id}
                              onClick={() => {
                                setSelectedSourceIds(prev => 
                                  prev.includes(source.id) 
                                    ? prev.filter(id => id !== source.id)
                                    : [...prev, source.id]
                                );
                                setPage(1);
                              }}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors"
                            >
                              <Checkbox checked={selectedSourceIds.includes(source.id)} />
                              <Package className="h-3 w-3" />
                              {source.name}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}

                  <Select value={hasNotesFilter} onValueChange={(v) => { setHasNotesFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-[160px]" data-testid="notes-filter">
                      <SelectValue placeholder="Notes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les notes</SelectItem>
                      <SelectItem value="yes">Avec notes</SelectItem>
                      <SelectItem value="no">Sans notes</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={markedFilter} onValueChange={(v) => { setMarkedFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-[160px]" data-testid="marked-filter">
                      <SelectValue placeholder="Marqueurs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="marked">
                        <span className="flex items-center gap-2">
                          <BookmarkCheck className="h-4 w-4 text-orange-500" />
                          Marqués
                        </span>
                      </SelectItem>
                      <SelectItem value="unmarked">Non marqués</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Row 2: Text filters and sort */}
                <div className="flex flex-wrap gap-3">
                  <Input
                    placeholder="Ville..."
                    value={cityFilter}
                    onChange={(e) => { setCityFilter(e.target.value); setPage(1); }}
                    className="w-[160px]"
                    data-testid="city-filter"
                  />

                  {/* Sort options */}
                  <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
                    <SelectTrigger className="w-[180px]" data-testid="sort-by-filter">
                      <SelectValue placeholder="Trier par" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="createdAt">Date d'import</SelectItem>
                      <SelectItem value="updatedAt">Date de modification</SelectItem>
                      <SelectItem value="firstName">Prénom</SelectItem>
                      <SelectItem value="lastName">Nom</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={sortOrder} onValueChange={(v) => { setSortOrder(v); setPage(1); }}>
                    <SelectTrigger className="w-[140px]" data-testid="sort-order-filter">
                      <SelectValue placeholder="Ordre" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Plus récent</SelectItem>
                      <SelectItem value="asc">Plus ancien</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Row 3: Date range filter */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Date d'import :</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Du</span>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                      className="w-[160px]"
                      data-testid="date-from-filter"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Au</span>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                      className="w-[160px]"
                      data-testid="date-to-filter"
                    />
                  </div>

                  {hasFilters && (
                    <Button type="button" variant="ghost" onClick={clearFilters}>
                      <X className="h-4 w-4 mr-2" />
                      Effacer tout
                    </Button>
                  )}
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {isAdmin && selectedLeads.length > 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4 flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium">
              {selectedLeads.length} lead{selectedLeads.length > 1 ? "s" : ""} sélectionné
              {selectedLeads.length > 1 ? "s" : ""}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="bulk-assign-button">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Attribuer à
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {activeSellers.map((seller) => (
                  <DropdownMenuItem
                    key={seller.id}
                    onClick={() => openAssignDialog(seller.id, false)}
                  >
                    {seller.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => openAssignDialog(null, true)}>
                  Retirer l'attribution
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={openAutoAssignDialog}
              data-testid="bulk-auto-assign-button"
            >
              <Shuffle className="h-4 w-4 mr-2" />
              Attribuer automatiquement
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setBulkDeleteDialogOpen(true)}
              data-testid="bulk-delete-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer ({selectedLeads.length})
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedLeads([])}
            >
              Annuler
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Aucun lead trouvé</h3>
              <p className="text-muted-foreground mb-4">
                {hasFilters
                  ? "Essayez de modifier vos filtres"
                  : "Commencez par ajouter votre premier lead"}
              </p>
              {!hasFilters && (
                <Button onClick={() => setFormDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un lead
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isAdmin && (
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedLeads.length === leads.length}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                    )}
                    <TableHead>Contact</TableHead>
                    <TableHead className="hidden md:table-cell">Coordonnées</TableHead>
                    <TableHead className="hidden lg:table-cell">
                      <div className="flex items-center gap-1">
                        <Cake className="h-4 w-4" />
                        Naissance
                      </div>
                    </TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="hidden lg:table-cell">Campagne</TableHead>
                    {isAdmin && <TableHead className="hidden xl:table-cell">Source</TableHead>}
                    {isAdmin && <TableHead className="hidden xl:table-cell">Vendeur</TableHead>}
                    <TableHead className="hidden lg:table-cell">
                      <button 
                        onClick={() => handleSort("createdAt")}
                        className="flex items-center hover:text-foreground transition-colors"
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        Import
                        <SortIcon field="createdAt" />
                      </button>
                    </TableHead>
                    <TableHead className="hidden xl:table-cell">
                      <button 
                        onClick={() => handleSort("updatedAt")}
                        className="flex items-center hover:text-foreground transition-colors"
                      >
                        <Clock className="h-4 w-4 mr-1" />
                        Modifié
                        <SortIcon field="updatedAt" />
                      </button>
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      className="table-row-hover"
                      data-testid={`lead-row-${lead.id}`}
                    >
                      {isAdmin && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedLeads.includes(lead.id)}
                            onCheckedChange={() => toggleSelectLead(lead.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell
                        className="cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                              {getInitials(`${lead.firstName} ${lead.lastName}`)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">
                                {lead.firstName} {lead.lastName}
                              </p>
                              {lead.isMarked && (
                                <BookmarkCheck className="h-4 w-4 text-orange-500" />
                              )}
                            </div>
                            {lead.noteCount > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <MessageSquare className="h-3 w-3" />
                                {lead.noteCount} note{lead.noteCount > 1 ? "s" : ""}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        className="hidden md:table-cell cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <div className="space-y-1">
                          {lead.email && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {lead.email}
                            </div>
                          )}
                          {lead.phone && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {lead.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell
                        className="hidden lg:table-cell text-muted-foreground text-sm cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        {formatBirthDate(lead.birthDate)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {/* Quick Status Change Popover */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="flex items-center gap-1 group"
                              disabled={updatingStatus === lead.id}
                              data-testid={`status-trigger-${lead.id}`}
                            >
                              {lead.statusName ? (
                                <Badge
                                  className="status-badge cursor-pointer pr-1"
                                  style={{
                                    backgroundColor: `${lead.statusColor}20`,
                                    color: lead.statusColor,
                                    borderColor: lead.statusColor,
                                  }}
                                >
                                  {lead.statusName}
                                  <ChevronDown className="h-3 w-3 ml-1 opacity-50 group-hover:opacity-100" />
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="cursor-pointer">
                                  Choisir
                                  <ChevronDown className="h-3 w-3 ml-1" />
                                </Badge>
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-48 p-1" align="start">
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground px-2 py-1 font-medium">
                                Changer le statut
                              </p>
                              {statuses.map((status) => (
                                <button
                                  key={status.id}
                                  onClick={() => handleQuickStatusChange(lead.id, status.id)}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors ${
                                    lead.statusId === status.id ? "bg-muted" : ""
                                  }`}
                                  data-testid={`status-option-${status.id}`}
                                >
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: status.color }}
                                  />
                                  {status.name}
                                  {lead.statusId === status.id && (
                                    <span className="ml-auto text-xs text-muted-foreground">✓</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                      <TableCell
                        className="hidden lg:table-cell cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        {lead.campaignName ? (
                          <Badge
                            variant="outline"
                            className="flex items-center gap-1 w-fit"
                            style={{
                              borderColor: lead.campaignColor,
                              color: lead.campaignColor,
                            }}
                          >
                            <Megaphone className="h-3 w-3" />
                            {lead.campaignName}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell
                          className="hidden xl:table-cell cursor-pointer"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                        >
                          {lead.sourceName ? (
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <Package className="h-3 w-3" />
                              {lead.sourceName}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      )}
                      {isAdmin && (
                        <TableCell
                          className="hidden xl:table-cell cursor-pointer"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                        >
                          {lead.assignedSellerName || (
                            <span className="text-muted-foreground">Non attribué</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell
                        className="hidden lg:table-cell text-muted-foreground text-sm cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <div className="flex items-center gap-1" title={formatDateTime(lead.createdAt)}>
                          {formatDate(lead.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell
                        className="hidden xl:table-cell text-muted-foreground text-sm cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <div className="flex items-center gap-1" title={formatDateTime(lead.updatedAt)}>
                          {formatDate(lead.updatedAt)}
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Quick marker button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleMarker(lead.id)}
                            className={`h-8 w-8 ${lead.isMarked ? "text-orange-500 hover:text-orange-600" : "text-muted-foreground hover:text-orange-500"}`}
                            title={lead.isMarked ? "Retirer le marqueur" : "Marquer ce lead"}
                          >
                            {lead.isMarked ? (
                              <BookmarkCheck className="h-4 w-4" />
                            ) : (
                              <Bookmark className="h-4 w-4" />
                            )}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}>
                                <Eye className="h-4 w-4 mr-2" />
                                Voir détails
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleMarker(lead.id)}>
                                {lead.isMarked ? (
                                  <>
                                    <BookmarkX className="h-4 w-4 mr-2" />
                                    Retirer le marqueur
                                  </>
                                ) : (
                                  <>
                                    <Bookmark className="h-4 w-4 mr-2" />
                                    Marquer
                                  </>
                                )}
                              </DropdownMenuItem>
                              {isAdmin && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => {
                                      setLeadToDelete(lead.id);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {totalCount > 0 ? (
              <>
                Affichage {Math.min((page - 1) * limit + 1, totalCount)} - {Math.min(page * limit, totalCount)} sur {totalCount}
              </>
            ) : (
              "Aucun résultat"
            )}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Par page:</span>
            <Select value={limit.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Page {page} sur {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce lead ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le lead et toutes ses notes seront
              définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedLeads.length} lead(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les leads sélectionnés et toutes leurs notes
              seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-bulk-delete"
            >
              Supprimer {selectedLeads.length} lead(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assignment Dialog with Status Reset Option */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isUnassigning 
                ? "Retirer l'attribution" 
                : `Attribuer à ${sellers.find(s => s.id === selectedSellerId)?.name || ""}`
              }
            </DialogTitle>
            <DialogDescription>
              {selectedLeads.length} lead{selectedLeads.length > 1 ? "s" : ""} sélectionné{selectedLeads.length > 1 ? "s" : ""}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex items-start space-x-3 p-4 rounded-lg border bg-muted/50">
              <Checkbox
                id="reset-status"
                checked={resetStatusOnAssign}
                onCheckedChange={(checked) => setResetStatusOnAssign(checked)}
                data-testid="reset-status-checkbox"
              />
              <div className="space-y-1">
                <Label 
                  htmlFor="reset-status" 
                  className="text-sm font-medium cursor-pointer flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Remettre le statut à "Nouveau"
                </Label>
                <p className="text-xs text-muted-foreground">
                  {isUnassigning 
                    ? "Les leads désattribués seront remis au statut initial pour être retraités."
                    : "Les leads attribués seront remis au statut initial pour que le vendeur commence un nouveau suivi."
                  }
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleBulkAssign} data-testid="confirm-assign-button">
              {isUnassigning ? "Retirer" : "Attribuer"}
              {resetStatusOnAssign && " et réinitialiser"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Assign Dialog */}
      <Dialog open={autoAssignDialogOpen} onOpenChange={setAutoAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shuffle className="h-5 w-5" />
              Attribution automatique
            </DialogTitle>
            <DialogDescription>
              Distribuer {selectedLeads.length} lead{selectedLeads.length > 1 ? "s" : ""} équitablement entre les vendeurs sélectionnés
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-sm font-medium mb-3 block">
                Sélectionner les vendeurs ({selectedSellerIds.length} sélectionné{selectedSellerIds.length > 1 ? "s" : ""})
              </Label>
              <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                {activeSellers.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">
                    Aucun vendeur actif
                  </p>
                ) : (
                  activeSellers.map((seller) => (
                    <button
                      key={seller.id}
                      onClick={() => toggleSellerForAutoAssign(seller.id)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors"
                    >
                      <Checkbox checked={selectedSellerIds.includes(seller.id)} />
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {getInitials(seller.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{seller.name}</span>
                      {selectedSellerIds.includes(seller.id) && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          ~{Math.ceil(selectedLeads.length / selectedSellerIds.length)} leads
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Shuffle option */}
            <div className="flex items-start space-x-3 p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/30">
              <Checkbox
                id="shuffle-leads"
                checked={shuffleBeforeAssign}
                onCheckedChange={(checked) => setShuffleBeforeAssign(checked)}
                data-testid="shuffle-leads-checkbox"
              />
              <div className="space-y-1">
                <Label 
                  htmlFor="shuffle-leads" 
                  className="text-sm font-medium cursor-pointer flex items-center gap-2"
                >
                  <Shuffle className="h-4 w-4 text-blue-600" />
                  Mixer le partage
                </Label>
                <p className="text-xs text-muted-foreground">
                  Chaque lead sera attribué à un vendeur DIFFÉRENT de son vendeur actuel.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 rounded-lg border bg-muted/50">
              <Checkbox
                id="reset-status-auto"
                checked={resetStatusOnAutoAssign}
                onCheckedChange={(checked) => setResetStatusOnAutoAssign(checked)}
                data-testid="reset-status-auto-checkbox"
              />
              <div className="space-y-1">
                <Label 
                  htmlFor="reset-status-auto" 
                  className="text-sm font-medium cursor-pointer flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Remettre le statut à "Nouveau"
                </Label>
                <p className="text-xs text-muted-foreground">
                  Les leads seront remis au statut initial pour un nouveau suivi.
                </p>
              </div>
            </div>

            {selectedSellerIds.length >= 2 && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
                <strong>Distribution :</strong> {selectedLeads.length} leads → {selectedSellerIds.length} vendeurs
                <br />
                <span className="text-green-600">
                  ≈ {Math.floor(selectedLeads.length / selectedSellerIds.length)} leads/vendeur
                  {selectedLeads.length % selectedSellerIds.length > 0 && 
                    ` (+1 pour ${selectedLeads.length % selectedSellerIds.length} vendeur${selectedLeads.length % selectedSellerIds.length > 1 ? "s" : ""})`
                  }
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoAssignDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAutoAssign} 
              disabled={selectedSellerIds.length === 0}
              data-testid="confirm-auto-assign-button"
            >
              <Shuffle className="h-4 w-4 mr-2" />
              Distribuer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Form Dialog */}
      <LeadFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        onSuccess={() => {
          setFormDialogOpen(false);
          fetchData();
        }}
        statuses={statuses}
        sellers={sellers}
        campaigns={campaigns}
      />
    </div>
  );
};
